import showNotificationOnMove from './helpers/showNotificationOnMove';
import { updateCountForItemMove } from './helpers/updateFolderCountForArchiveMailbox';
import moveItemsService from '../../services/moveItemsService';
import datapoints from '../datapoints';
import handleBlockActionForShadowMailbox from '../helpers/handleBlockActionForShadowMailbox';
import isDeletedOrUnderDeletedItemsFolder from '../helpers/isDeletedOrUnderDeletedItemsFolder';
import shouldPerformRowRemovalInstantUpdate from '../helpers/shouldPerformRowRemovalInstantUpdate';
import { DatapointStatus, returnTopExecutingActionDatapoint } from 'owa-analytics';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import deleteItemsStoreUpdate from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';
import { shouldUpdateFolderCount } from 'owa-mail-list-actions';
import { getRowKeysFromRowIds, isItemOfMessageType, TableView } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { mailStore } from 'owa-mail-store';
import { processMoveOrCopyItemActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import { doesTableSupportUndo } from 'owa-mail-undo';
import * as undoActions from 'owa-mail-undo';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';

export default createLegacyActionWithDatapoint('moveItemsFromItemIds', datapoints.moveItems)(
    function moveItemsFromItemIds(
        itemIds: string[],
        destinationFolderId: string,
        originalTableView: TableView,
        actionSource: ActionSource
    ): Promise<void> {
        const sourceFolderId = originalTableView.tableQuery.folderId;
        const allowUndo = doesTableSupportUndo(sourceFolderId, getMailboxInfo(originalTableView));
        const moveDatapoint = returnTopExecutingActionDatapoint();

        if (handleBlockActionForShadowMailbox(sourceFolderId, destinationFolderId)) {
            moveDatapoint.endWithError(
                DatapointStatus.UserError,
                new Error('Action not supported for shadow mailbox')
            );
            return Promise.resolve();
        }
        const rowKeys = getRowKeysFromRowIds(itemIds, originalTableView);
        undoActions.clearLastUndoableAction();

        const itemContexts = itemIds.map(itemIdValue => {
            const item = mailStore.items.get(itemIdValue);
            let isReadValue;

            if (isItemOfMessageType(item)) {
                isReadValue = (item as Message).IsRead;
            } else {
                // Default to isRead = true when not a mail item
                isReadValue = true;
            }

            return {
                itemId: itemIdValue,
                conversationId: item.ConversationId.Id,
                isRead: isReadValue,
                mailboxInfo: item.MailboxInfo,
            };
        });

        /**
         * If folder is under archive mailbox or shared folder, update folder count manually in local,
         * since archive mbx and shared folder don't have hierarchy notification
         * unread count won't get updated instantly
         */
        shouldUpdateFolderCount(destinationFolderId) &&
            updateCountForItemMove(itemContexts, destinationFolderId);

        // Perform instant update
        if (
            shouldPerformRowRemovalInstantUpdate(
                originalTableView.tableQuery,
                isDeletedOrUnderDeletedItemsFolder(destinationFolderId)
            )
        ) {
            deleteItemsStoreUpdate(itemContexts, sourceFolderId);
        }

        // Make service request
        const moveItemsPromise = moveItemsService(itemIds, destinationFolderId, sourceFolderId);

        if (allowUndo) {
            undoActions.addActionToUndoStack(async () => {
                const moveItemsResponse = await moveItemsPromise;

                if (moveItemsResponse.ResponseMessages) {
                    const responseItems = <ItemInfoResponseMessage[]>(
                        moveItemsResponse.ResponseMessages.Items
                    );
                    const movedItemIds = [];

                    for (const responseItem of responseItems) {
                        if (responseItem.ResponseClass == 'Success') {
                            movedItemIds.push(responseItem.Items[0].ItemId.Id);
                        }
                    }

                    if (movedItemIds.length > 0) {
                        // Reverse source folder id and destination folder id for undo
                        return moveItemsService(movedItemIds, sourceFolderId, destinationFolderId);
                    }
                }
                return Promise.resolve();
            }, originalTableView.serverFolderId);
        }

        showNotificationOnMove(itemIds.length, destinationFolderId, allowUndo);

        moveDatapoint.markUserPerceivedTime();

        return processMoveOrCopyItemActionResponseMessage(
            moveItemsPromise,
            rowKeys,
            sourceFolderId,
            originalTableView,
            true /* shouldRemoveRowsFromTombstone */
        );
    }
);
